import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import { Target, DollarSign, Calendar, Settings, Play, Trash2, AlertCircle, ShoppingBag, X, Search, RefreshCw, LayoutTemplate, Zap, Activity, Eye, TrendingUp, ChevronLeft, ChevronRight, BarChart2, MousePointerClick, Filter, ChevronDown, ChevronUp, PenTool, Image as ImageIcon, Film, Pause } from 'lucide-react';
import toast from 'react-hot-toast';
import AdsBoosterSettings from './AdsBoosterSettings';
import CampaignCreator from './CampaignCreator';
import AdsBoosterPosts from './AdsBoosterPosts';
import CustomProductCreator from './CustomProductCreator';

interface Campaign {
  id: string;
  name: string;
  objective: string;
  daily_budget: number;
  start_date: string;
  end_date: string;
  status: string;
  error_message?: string;
  source_type: string;
  meta_campaign_id?: string;
  metrics: { impressions?: number; clicks?: number; spend?: number; reach?: number; ctr?: number; cpc?: number; leads?: number; purchases?: number; landing_page_views?: number; roas?: number; last_synced_at?: string };
  publish_log?: Array<{ step: string; ts: string; msg: string; meta_id?: string }>;
  created_at: string;
}

interface WooProduct {
  id: number; store_id?: string; store_name?: string; name: string; price: string; permalink: string; image_url: string | null; stock_status: string;
}

export default function AdsBooster() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'creator' | 'posts' | 'products' | 'custom_products' | 'campaigns' | 'settings'>(
    () => (localStorage.getItem('adsBoosterTab') as any) || 'creator'
  );

  useEffect(() => {
    localStorage.setItem('adsBoosterTab', activeTab);
  }, [activeTab]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [products, setProducts] = useState<WooProduct[]>([]);
  const [connectedStores, setConnectedStores] = useState<{id: string, store_name: string}[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedStoreFilter, setSelectedStoreFilter] = useState('all');
  const [socialAccounts, setSocialAccounts] = useState<any[]>([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [errorModal, setErrorModal] = useState<{ show: boolean; error: any; log?: any[] } | null>(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<WooProduct | null>(null);
  const [selectedCarouselProducts, setSelectedCarouselProducts] = useState<WooProduct[]>([]);
  const [prodBudget, setProdBudget] = useState(20);
  const [prodDuration, setProdDuration] = useState(7);
  const [isCarousel, setIsCarousel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(null);
  const [expandedCreatives, setExpandedCreatives] = useState<any[]>([]);
  const [isSyncingStats, setIsSyncingStats] = useState(false);
  const itemsPerPage = 20;

  // Auto-sync stats every 60 seconds when on campaigns tab
  useEffect(() => {
    let interval: any;
    if (user && activeTab === 'campaigns') {
      interval = setInterval(async () => {
        try {
          setIsSyncingStats(true);
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) return;
          const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-campaigns-sync`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${session.access_token}` },
          });
          if (res.ok) {
            // Silently fetch updated campaigns
            const { data } = await supabase.from('ad_campaigns').select('*').eq('user_id', user.id).order('created_at', { ascending: false });
            if (data) setCampaigns(data);
          }
        } catch (e) {
          console.error('Stats sync error:', e);
        } finally {
          setIsSyncingStats(false);
        }
      }, 60000);
    }
    return () => clearInterval(interval);
  }, [user, activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedStoreFilter]);

  useEffect(() => {
    if (user) {
      if (activeTab === 'campaigns') fetchCampaigns();
      if (activeTab === 'products') fetchProducts();
      
      supabase.from('social_accounts')
        .select('*')
        .eq('owner_user_id', user.id)
        .eq('platform', 'facebook')
        .then(({ data }) => {
          if (data) {
            setSocialAccounts(data);
            if (data.length > 0) {
              setSelectedPageId(data[0].page_id);
            }
          }
        });
    }
  }, [user, activeTab]);

  const fetchCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      const { data, error } = await supabase.from('ad_campaigns').select('*').eq('user_id', user?.id).order('created_at', { ascending: false });
      if (error) throw error;
      setCampaigns(data || []);
    } catch (e) { console.error(e); } finally { setLoadingCampaigns(false); }
  };

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-woo-products`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      const result = await res.json();
      
      // Niezależne pobranie listy sklepów bezpośrednio z bazy danych
      const { data: storesData } = await supabase
        .from('woocommerce_stores')
        .select('id, store_name')
        .eq('user_id', session.user.id)
        .eq('is_active', true);
        
      if (storesData && storesData.length > 0) {
        setConnectedStores(storesData);
      } else if (result.stores) {
        setConnectedStores(result.stores);
      }

      if (!res.ok) {
        if (result.error?.includes('credentials not found')) { toast.error('Skonfiguruj WooCommerce w Ustawieniach'); setActiveTab('settings'); }
        else throw new Error(result.error);
        return;
      }
      setProducts(result.products || []);
    } catch (e) { console.error(e); toast.error('Błąd pobierania produktów'); } finally { setLoadingProducts(false); }
  };

  const publishProductCampaign = async () => {
    if ((!selectedProduct && !isCarousel) || (isCarousel && selectedCarouselProducts.length === 0) || !user) return;
    try {
      toast.loading(isCarousel ? 'Tworzenie kampanii karuzelowej...' : 'Tworzenie kampanii produktowej...', { id: 'prodcamp' });
      const endDate = new Date(); endDate.setDate(endDate.getDate() + prodDuration);

      // ── Campaign naming ──
      const campaignName = isCarousel 
        ? `Karuzela: ${selectedCarouselProducts.length} Produktów Woo` 
        : `Produkt: ${selectedProduct!.name}`;

      // ── Destination URL logic ──
      // Single product: link to the SPECIFIC PRODUCT page
      // Carousel: link to the STORE HOME (each carousel card has its own product link)
      const destUrl = isCarousel 
        ? new URL(selectedCarouselProducts[0].permalink).origin 
        : selectedProduct!.permalink;

      // Intelligent Targeting Logic
      const getIntelligentTargeting = (productName: string) => {
        const name = productName.toLowerCase();
        let age_min = 25; // Default higher purchasing power for products
        let age_max = 65;
        let genders = [0]; // All
        
        if (name.includes('zabieg') || name.includes('spa') || name.includes('twarz') || name.includes('krem')) {
          age_min = 20;
          age_max = 55;
          genders = [2]; // Female
        } else if (name.includes('lampa') || name.includes('żarówka') || name.includes('oczko') || name.includes('stojak') || name.includes('meble')) {
          age_min = 28; // Homeowners / interior design focus
          age_max = 65;
        } else if (name.includes('kabel') || name.includes('usb') || name.includes('elektronika')) {
          age_min = 18;
          age_max = 50;
        }
        
        return {
          age_min,
          age_max,
          genders,
          geo_locations: { countries: ['PL'] }
        };
      };

      const targeting = isCarousel 
        ? getIntelligentTargeting(selectedCarouselProducts[0].name)
        : getIntelligentTargeting(selectedProduct!.name);

      if (!selectedPageId) throw new Error('Wybierz Stronę (Fanpage) z której ma zostać opublikowana reklama');

      const { data: camp, error: campErr } = await supabase.from('ad_campaigns').insert({
        user_id: user.id, name: campaignName, objective: 'OUTCOME_SALES',
        daily_budget: prodBudget, start_date: new Date().toISOString(), end_date: endDate.toISOString(),
        destination_url: destUrl, targeting: targeting,
        status: 'draft', source_type: 'woo_product', product_data: isCarousel ? selectedCarouselProducts : selectedProduct,
        meta_page_id: selectedPageId,
      }).select('id').single();
      if (campErr || !camp) throw new Error(campErr?.message || 'Insert failed');

      if (isCarousel) {
        // ── CAROUSEL: Enrich each product with proper image_url and permalink ──
        const enrichedProducts = selectedCarouselProducts.map((p: any) => ({
          ...p,
          image_url: p.image_url || p.images?.[0]?.src || '',
          permalink: p.permalink || destUrl,
          name: p.name || 'Produkt',
          price: p.price || '0',
        }));

        await supabase.from('ad_campaign_creatives').insert({
          campaign_id: camp.id, media_url: 'carousel', media_type: 'carousel',
          primary_text: `🛒 Sprawdź nasze bestsellery! ${enrichedProducts.length} produktów w świetnych cenach. Kliknij i zamów!`,
          headline: 'Katalog Produktów', sort_order: 0,
          carousel_data: enrichedProducts,
        });
      } else if (selectedProduct) {
        // ── SINGLE PRODUCT: Full product info with link ──
        const p = selectedProduct;
        const priceFormatted = p.price ? parseFloat(p.price).toFixed(2) : '—';
        
        // Generate multiple creative variants for A/B testing
        const variants = [
          `${p.name} — teraz za ${priceFormatted} PLN! Kliknij i sprawdź ofertę.`,
          `🔥 Bestseller: ${p.name}! Zamów już dziś w rewelacyjnej cenie ${priceFormatted} PLN.`,
          `Odkryj ${p.name}! Tylko ${priceFormatted} PLN. Nie przegap tej okazji, kliknij i kup teraz!`
        ];

        const creativesToInsert = variants.map((text, idx) => ({
          campaign_id: camp.id, 
          media_url: p.image_url || p.images?.[0]?.src || '', 
          media_type: 'image',
          primary_text: text,
          headline: p.name, 
          sort_order: idx,
        }));

        await supabase.from('ad_campaign_creatives').insert(creativesToInsert);
      }

      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-campaign-publisher`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ campaign_id: camp.id }),
      });
      const result = await res.json();

      if (!res.ok) {
        // Show error with pipeline log if available
        toast.dismiss('prodcamp');
        setErrorModal({ show: true, error: result.error || 'Błąd publikacji', log: result.log || [] });
        fetchCampaigns();
        setShowBudgetModal(false); setSelectedProduct(null); setSelectedCarouselProducts([]); setIsCarousel(false); setActiveTab('campaigns');
        return;
      }

      if (result.success) {
        toast.success(isCarousel ? '🎠 Karuzela AKTYWNA!' : '✅ Kampania produktowa AKTYWNA!', { id: 'prodcamp', duration: 5000 });
      } else {
        toast.dismiss('prodcamp');
        setErrorModal({ show: true, error: 'Niektóre kreacje nie zostały opublikowane', log: result.log || [] });
      }
      setShowBudgetModal(false); setSelectedProduct(null); setSelectedCarouselProducts([]); setIsCarousel(false); setActiveTab('campaigns');
      fetchCampaigns();
    } catch (e) { console.error(e); toast.error(e instanceof Error ? e.message : 'Błąd', { id: 'prodcamp' }); }
  };

  const deleteCampaign = async (id: string) => {
    if (!confirm('Usunąć kampanię z panelu?')) return;
    const { error } = await supabase.from('ad_campaigns').delete().eq('id', id);
    if (error) toast.error('Błąd usuwania'); else { toast.success('Usunięto'); fetchCampaigns(); }
  };

  const retryCampaign = async (id: string) => {
    try {
      toast.loading('Ponawianie publikacji w Meta Ads...', { id: 'retry' });
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-campaign-publisher`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ campaign_id: id }),
      });
      
      let result: any;
      try { result = await res.json(); } catch { throw new Error('Błąd odpowiedzi serwera'); }
      
      if (!res.ok) {
        toast.dismiss('retry');
        setErrorModal({ show: true, error: result.error || 'Błąd publikacji', log: result.log || [] });
        fetchCampaigns();
        return;
      }
      
      toast.success('🚀 Publikacja zakończona sukcesem!', { id: 'retry', duration: 4000 });
      fetchCampaigns();
    } catch (e: any) {
      toast.error(e.message || 'Błąd serwera', { id: 'retry' });
    }
  };

  const getStatusBadge = (status: string) => {
    const m: Record<string, { cls: string; label: string }> = {
      draft: { cls: 'bg-slate-500/10 text-slate-400 border-slate-500/20', label: 'Szkic' },
      publishing: { cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20', label: 'Publikowanie...' },
      active: { cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', label: 'Aktywna' },
      pending_payment: { cls: 'bg-orange-500/10 text-orange-400 border-orange-500/20', label: 'Oczekuje na płatność' },
      completed: { cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20', label: 'Zakończona' },
      failed: { cls: 'bg-rose-500/10 text-rose-400 border-rose-500/20', label: 'Błąd Meta API' },
      paused: { cls: 'bg-slate-500/10 text-slate-400 border-slate-500/20', label: 'Wstrzymana' },
    };
    const s = m[status] || m.draft;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${s.cls} flex items-center gap-1.5`}>
        {status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />}
        {status === 'publishing' && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
        {status === 'pending_payment' && <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />}
        {s.label}
      </span>
    );
  };

  const tabs = [
    { id: 'creator', icon: LayoutTemplate, label: 'Kreator Kampanii' },
    { id: 'posts', icon: TrendingUp, label: 'Promocja Postów' },
    { id: 'products', icon: ShoppingBag, label: 'Z Produktów Woo' },
    { id: 'custom_products', icon: PenTool, label: 'Twój Produkt' },
    { id: 'campaigns', icon: Activity, label: 'Aktywne Kampanie' },
    { id: 'settings', icon: Settings, label: 'Ustawienia Ads' },
  ];

  return (
    <div className="space-y-8 pb-12 bg-[#020617] min-h-screen text-slate-200 p-1 rounded-3xl">
      {/* Header */}
      <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#0f172a] via-[#0b0f19] to-[#020617] p-10 border border-slate-800/60 shadow-2xl">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-600/20 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-600/20 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-[2px] shadow-lg shadow-indigo-500/20">
              <div className="w-full h-full bg-[#0f172a] rounded-2xl flex items-center justify-center">
                <Zap className="w-7 h-7 text-blue-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-indigo-200 tracking-tight">
              Marketing Studio PRO
            </h1>
          </div>
          <p className="text-lg text-slate-400 leading-relaxed font-medium">
            Profesjonalny kreator kampanii reklamowych Meta Ads (v21.0). Pipeline: Campaign → AdSet → Media Upload → AdCreative → Ad.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(t => {
          const Icon = t.icon; const isActive = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id as any)}
              className={`flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold transition-all whitespace-nowrap ${isActive ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-[0_0_20px_rgba(79,70,229,0.3)] border border-indigo-500/50' : 'bg-[#0f172a] text-slate-400 hover:text-white hover:bg-[#1e293b] border border-slate-800'}`}>
              <Icon className={`w-5 h-5 ${isActive ? 'text-blue-200' : ''}`} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="min-h-[500px]">
        {/* Creator */}
        {activeTab === 'creator' && <CampaignCreator onCreated={() => { setActiveTab('campaigns'); fetchCampaigns(); }} />}

        {/* Posts */}
        {activeTab === 'posts' && <AdsBoosterPosts />}

        {/* Products */}
        {activeTab === 'products' && (
          <div className="space-y-6 animate-in fade-in">
            <div className="flex flex-col md:flex-row md:items-center justify-between bg-[#0f172a] p-4 rounded-2xl border border-slate-800 gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-500/10 rounded-lg"><ShoppingBag className="w-5 h-5 text-indigo-400" /></div>
                <h2 className="text-xl font-bold text-white">Katalog WooCommerce</h2>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                {connectedStores.length > 0 && (
                  <select
                    value={selectedStoreFilter}
                    onChange={(e) => setSelectedStoreFilter(e.target.value)}
                    className="pl-4 pr-10 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2394a3b8%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[position:right_1rem_center] bg-[length:0.75rem_auto] cursor-pointer hover:bg-slate-800 transition-colors"
                  >
                    <option value="all">Wszystkie Sklepy</option>
                    {connectedStores.map(store => (
                      <option key={store.id} value={store.store_name}>{store.store_name}</option>
                    ))}
                  </select>
                )}
                <div className="relative flex-1 md:w-64">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="text" placeholder="Szukaj..." value={searchText} onChange={e => setSearchText(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-slate-500 transition-colors" />
                </div>
                <button onClick={fetchProducts} className="p-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl border border-slate-700">
                  <RefreshCw className={`w-4 h-4 ${loadingProducts ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            {loadingProducts ? (
              <div className="flex items-center justify-center h-64"><div className="w-12 h-12 border-4 border-slate-700 border-t-indigo-500 rounded-full animate-spin" /></div>
            ) : products.length === 0 ? (
              <div className="bg-[#0f172a] rounded-3xl p-16 text-center border border-slate-800 flex flex-col items-center">
                <ShoppingBag className="w-10 h-10 text-slate-500 mb-4" /><h3 className="text-xl font-bold text-white mb-2">Brak produktów</h3>
                <p className="text-slate-400">Sprawdź klucze REST w Ustawieniach.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedCarouselProducts.length > 1 && (
                  <div className="bg-indigo-600/20 border border-indigo-500 p-4 rounded-2xl flex justify-between items-center sticky top-4 z-20 shadow-xl backdrop-blur-xl">
                    <div>
                      <p className="text-white font-bold">Wybrano do karuzeli: {selectedCarouselProducts.length} (max 10)</p>
                      <p className="text-indigo-200 text-xs">Utwórz dynamiczną karuzelę ze zdjęciami, nazwami i linkami wprost z WooCommerce.</p>
                    </div>
                    <button onClick={() => { setIsCarousel(true); setShowBudgetModal(true); }} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl whitespace-nowrap transition-transform hover:scale-105 shadow-[0_0_15px_rgba(79,70,229,0.5)]">
                      Stwórz Karuzelę
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {(() => {
                    const filteredProducts = products
                      .filter(p => selectedStoreFilter === 'all' || p.store_name === selectedStoreFilter)
                      .filter(p => p.name.toLowerCase().includes(searchText.toLowerCase()));
                    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
                    const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
                    
                    if (filteredProducts.length === 0) {
                      return (
                        <div className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 text-center py-12">
                          <ShoppingBag className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                          <h3 className="text-xl font-bold text-slate-300">Brak wyników</h3>
                          <p className="text-slate-500 mt-2">Brak produktów dla wybranego sklepu lub wyszukiwania.</p>
                        </div>
                      );
                    }
                    
                    return (
                      <>
                        {paginatedProducts.map(p => {
                    const isSelected = selectedCarouselProducts.some(sp => sp.id === p.id);
                    return (
                    <div key={p.id} className={`group bg-[#0f172a] rounded-[1.5rem] border ${isSelected ? 'border-indigo-500 shadow-[0_0_15px_rgba(79,70,229,0.3)]' : 'border-slate-800'} hover:border-indigo-500/50 transition-all overflow-hidden flex flex-col`}>
                      <div className="relative h-56 bg-white/5 p-4 flex items-center justify-center cursor-pointer" onClick={() => {
                        if (isSelected) setSelectedCarouselProducts(prev => prev.filter(sp => sp.id !== p.id));
                        else if (selectedCarouselProducts.length < 10) setSelectedCarouselProducts([...selectedCarouselProducts, p]);
                        else toast.error('Maksymalnie 10 produktów w karuzeli Meta');
                      }}>
                        <div className="absolute top-3 right-3 z-20">
                          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-slate-500 bg-black/50'}`}>
                            {isSelected && <Target className="w-4 h-4 text-white" />}
                          </div>
                        </div>
                        {p.store_name && (
                          <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur text-indigo-300 text-[10px] font-bold rounded uppercase z-10 border border-indigo-500/30">
                            {p.store_name}
                          </div>
                        )}
                        {p.image_url ? (
                          <img 
                            src={p.image_url} 
                            alt={p.name} 
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="%23475569" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';
                              target.className = 'w-12 h-12 opacity-50';
                            }}
                            className={`w-full h-full object-contain transition-transform duration-500 ${isSelected ? 'scale-110' : 'group-hover:scale-105'}`} 
                          />
                        ) : <ShoppingBag className="w-12 h-12 text-slate-600" />}
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="font-bold text-white text-sm mb-1 line-clamp-2">{p.name}</h3>
                        <p className="text-indigo-400 font-extrabold text-lg mb-4">{parseFloat(p.price).toFixed(2)} PLN</p>
                        <button onClick={() => { setIsCarousel(false); setSelectedProduct(p); setShowBudgetModal(true); }}
                          className="mt-auto w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-indigo-500 rounded-xl font-bold transition-all">
                          <Target className="w-4 h-4 text-indigo-400" /> Promuj Sam
                        </button>
                      </div>
                    </div>
                  )})}
                      {totalPages > 1 && (
                        <div className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 flex items-center justify-center gap-2 md:gap-4 mt-8 pt-6 border-t border-slate-800">
                          <button 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-2.5 rounded-xl bg-slate-800 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-700 hover:text-indigo-300 transition-colors font-bold text-sm md:text-base"
                          >
                            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" /> Poprzednia
                          </button>
                          
                          <div className="flex gap-1">
                            {Array.from({ length: totalPages }).map((_, i) => {
                              const pageNum = i + 1;
                              if (totalPages > 5 && pageNum !== 1 && pageNum !== totalPages && Math.abs(pageNum - currentPage) > 1) {
                                if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                  return <span key={i} className="text-slate-500 px-1 md:px-2 py-2">...</span>;
                                }
                                return null;
                              }
                              return (
                                <button
                                  key={i}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={`w-8 h-8 md:w-10 md:h-10 text-sm md:text-base rounded-xl font-bold transition-colors ${currentPage === pageNum ? 'bg-indigo-600 text-white shadow-[0_0_10px_rgba(79,70,229,0.5)]' : 'bg-[#0f172a] text-slate-400 hover:bg-slate-800 border border-slate-800'}`}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>

                          <button 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-2.5 rounded-xl bg-slate-800 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-700 hover:text-indigo-300 transition-colors font-bold text-sm md:text-base"
                          >
                            Następna <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                        </div>
                      )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Custom Products */}
        {activeTab === 'custom_products' && (
          <CustomProductCreator onCreated={() => { setActiveTab('campaigns'); fetchCampaigns(); }} />
        )}

        {/* Campaigns */}
        {activeTab === 'campaigns' && (
          <div className="space-y-6 animate-in fade-in">
            <div className="flex items-center justify-between bg-[#0f172a] p-4 rounded-2xl border border-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg"><Activity className="w-5 h-5 text-emerald-400" /></div>
                <h2 className="text-xl font-bold text-white">Centrum Kampanii</h2>
                {isSyncingStats && <span className="text-xs text-emerald-400 animate-pulse ml-2 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Auto-Sync</span>}
              </div>
              <button onClick={async () => {
                setIsSyncingStats(true);
                const { data: { session } } = await supabase.auth.getSession();
                if (session) {
                  await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-campaigns-sync`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${session.access_token}` },
                  });
                }
                await fetchCampaigns();
                setIsSyncingStats(false);
                toast.success('Statystyki zaktualizowane');
              }} className="p-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl border border-slate-700 transition-colors">
                <RefreshCw className={`w-4 h-4 ${loadingCampaigns || isSyncingStats ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {loadingCampaigns ? (
              <div className="flex items-center justify-center h-64"><div className="w-12 h-12 border-4 border-slate-700 border-t-emerald-500 rounded-full animate-spin" /></div>
            ) : campaigns.length === 0 ? (
              <div className="bg-[#0f172a] rounded-3xl p-16 text-center border border-slate-800 flex flex-col items-center">
                <Target className="w-10 h-10 text-slate-500 mb-4" /><h3 className="text-xl font-bold text-white mb-2">Brak kampanii</h3>
                <p className="text-slate-400">Stwórz nową w Kreatorze lub z Produktów.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5">
                {campaigns.map(c => {
                  const isExpanded = expandedCampaignId === c.id;
                  const m = c.metrics || {};
                  return (
                  <div key={c.id} className="bg-[#0f172a] rounded-2xl border border-slate-800 hover:border-slate-700 transition-all flex flex-col overflow-hidden">
                    {/* Header Row (Clickable) */}
                    <div 
                      onClick={async () => {
                        const newId = isExpanded ? null : c.id;
                        setExpandedCampaignId(newId);
                        if (newId) {
                          const { data: cr } = await supabase.from('ad_campaign_creatives').select('*').eq('campaign_id', c.id).order('sort_order', { ascending: true });
                          setExpandedCreatives(cr || []);
                        } else {
                          setExpandedCreatives([]);
                        }
                      }}
                      className="p-6 flex flex-col xl:flex-row gap-6 items-start xl:items-center cursor-pointer hover:bg-slate-800/30 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-slate-800/50 text-slate-400 border-slate-700">
                            {c.source_type === 'woo_product' ? 'Produkt Woo' : 'Kreator'}
                          </span>
                          {getStatusBadge(c.status)}
                        </div>
                        <h3 className="text-lg font-bold text-white leading-tight line-clamp-1">{c.name}</h3>
                        {c.meta_campaign_id && <p className="text-xs text-slate-500 mt-1 font-mono">Meta ID: {c.meta_campaign_id}</p>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full xl:w-auto">
                        {[
                          { label: 'Budżet/dzień', val: `${c.daily_budget} PLN`, icon: DollarSign, color: 'text-emerald-400' },
                          { label: 'Okres', val: `${Math.ceil((new Date(c.end_date).getTime() - new Date(c.start_date).getTime()) / 86400000)} dni`, icon: Calendar, color: 'text-blue-400' },
                          { label: c.objective === 'OUTCOME_LEADS' ? 'Leady' : 'Zasięg', val: String(c.objective === 'OUTCOME_LEADS' ? (m.leads || 0) : (m.reach || 0)), icon: c.objective === 'OUTCOME_LEADS' ? Target : Eye, color: 'text-purple-400' },
                          { label: 'Wydano', val: `${m.spend || 0} PLN`, icon: Activity, color: 'text-rose-400' },
                        ].map((stat, i) => (
                          <div key={i} className="bg-[#1e293b]/50 p-3 rounded-xl border border-slate-800">
                            <div className="flex items-center justify-between mb-1"><p className="text-[10px] font-bold text-slate-500 uppercase">{stat.label}</p><stat.icon className={`w-3.5 h-3.5 ${stat.color}`} /></div>
                            <p className="text-lg font-black text-white">{stat.val}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Stats View */}
                    {isExpanded && (
                      <div className="bg-[#0b0f19] border-t border-slate-800 p-6 animate-in slide-in-from-top-2">
                        <div className="flex items-center justify-between mb-6">
                          <h4 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                            <BarChart2 className="w-4 h-4 text-indigo-400" /> Pełne Statystyki Meta Ads
                          </h4>
                          {m.last_synced_at && (
                            <span className="text-xs text-slate-500">Zaktualizowano: {new Date(m.last_synced_at).toLocaleTimeString()}</span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                          {[
                            { label: 'Wyświetlenia', val: m.impressions || 0, icon: Eye, color: 'text-blue-400' },
                            { label: 'Zasięg', val: m.reach || 0, icon: Target, color: 'text-indigo-400' },
                            { label: 'Kliknięcia', val: m.clicks || 0, icon: MousePointerClick, color: 'text-emerald-400' },
                            { label: 'CTR', val: `${m.ctr || 0}%`, icon: Filter, color: 'text-amber-400' },
                            { label: 'CPC', val: `${m.cpc || 0} PLN`, icon: DollarSign, color: 'text-orange-400' },
                            { label: 'Koszt / Akcję (CPA)', val: `${m.spend && (m.purchases || m.leads) ? (m.spend / (m.purchases || m.leads)).toFixed(2) : 0} PLN`, icon: Activity, color: 'text-rose-400' },
                            { label: 'ROAS', val: m.roas ? `${m.roas}x` : '—', icon: TrendingUp, color: 'text-emerald-400' },
                            { label: 'Zakupy', val: m.purchases || 0, icon: ShoppingBag, color: 'text-purple-400' },
                            { label: 'Landing Page Views', val: m.landing_page_views || 0, icon: Eye, color: 'text-cyan-400' },
                            { label: 'Leady', val: m.leads || 0, icon: Target, color: 'text-fuchsia-400' },
                          ].map((stat, i) => (
                            <div key={i} className="bg-[#0f172a] p-4 rounded-xl border border-slate-800/50 shadow-inner">
                              <div className="flex items-center gap-2 mb-2">
                                <stat.icon className={`w-4 h-4 ${stat.color}`} />
                                <span className="text-xs font-bold text-slate-400">{stat.label}</span>
                              </div>
                              <p className="text-xl font-black text-white">{stat.val}</p>
                            </div>
                          ))}
                        </div>

                        {/* Per-Creative Stats Table */}
                        {expandedCreatives.length > 0 && (
                          <div className="mb-6">
                            <h4 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2 mb-3">
                              <ImageIcon className="w-4 h-4 text-purple-400" /> Kreacje ({expandedCreatives.length})
                            </h4>
                            <div className="space-y-2">
                              {expandedCreatives.map((cr: any, idx: number) => {
                                const cm = cr.metrics || {};
                                const isActive = cr.status === 'active';
                                const isPaused = cr.status === 'paused';
                                const isOptPaused = isPaused && cr.error_message?.includes('Optimizer');
                                return (
                                  <div key={cr.id} className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                                    isActive ? 'bg-[#0f172a] border-slate-800/50' :
                                    isOptPaused ? 'bg-amber-500/5 border-amber-500/20' :
                                    'bg-slate-800/20 border-slate-800/30 opacity-60'
                                  }`}>
                                    {/* Thumbnail */}
                                    <div className="w-12 h-12 rounded-lg bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center shrink-0">
                                      {cr.media_url && cr.media_url !== 'carousel' ? (
                                        cr.media_type === 'video' ? <Film className="w-5 h-5 text-slate-500" /> :
                                        <img src={cr.media_url} className="w-full h-full object-cover" />
                                      ) : <ImageIcon className="w-5 h-5 text-slate-600" />}
                                    </div>
                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-bold text-white truncate">{cr.headline || `Kreacja #${idx+1}`}</p>
                                      <p className="text-xs text-slate-500 truncate">{cr.primary_text?.substring(0, 60)}</p>
                                    </div>
                                    {/* Status Badge */}
                                    <div className="shrink-0">
                                      {isOptPaused ? (
                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-1">
                                          <Pause className="w-3 h-3" /> Optimizer
                                        </span>
                                      ) : isPaused ? (
                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-500/10 text-slate-400 border border-slate-500/20">Wstrzymana</span>
                                      ) : cr.status === 'failed' ? (
                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20">Błąd</span>
                                      ) : (
                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" /> Aktywna
                                        </span>
                                      )}
                                    </div>
                                    {/* Mini Metrics */}
                                    <div className="hidden md:flex items-center gap-4 text-xs shrink-0">
                                      <div className="text-center">
                                        <p className="text-slate-500 font-bold">Wydano</p>
                                        <p className="text-white font-black">{cm.spend?.toFixed(2) || '0'} PLN</p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-slate-500 font-bold">CTR</p>
                                        <p className="text-white font-black">{cm.ctr?.toFixed(2) || '0'}%</p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-slate-500 font-bold">CPA</p>
                                        <p className={`font-black ${cm.cpa ? 'text-white' : 'text-slate-600'}`}>{cm.cpa ? `${cm.cpa.toFixed(2)} PLN` : '—'}</p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-slate-500 font-bold">Wyniki</p>
                                        <p className="text-white font-black">{(cm.leads || 0) + (cm.purchases || 0)}</p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 w-full">
                          {(c.status === 'failed' || c.status === 'pending_payment') && (
                            <button onClick={(e) => { e.stopPropagation(); setErrorModal({ show: true, error: c.error_message, log: c.publish_log }) }}
                              className={`flex-1 px-4 py-2.5 ${c.status === 'pending_payment' ? 'bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border-orange-500/30' : 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 border-rose-500/30'} rounded-xl text-sm font-bold border flex items-center justify-center gap-1.5`}>
                              <AlertCircle className="w-4 h-4" /> {c.status === 'pending_payment' ? 'Szczegóły płatności' : 'Logi błędów'}
                            </button>
                          )}
                          {(c.status === 'failed' || c.status === 'draft') && (
                            <button onClick={(e) => { e.stopPropagation(); retryCampaign(c.id); }}
                              className="flex-1 px-4 py-2.5 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 rounded-xl text-sm font-bold border border-indigo-500/30 hover:border-indigo-500/50 flex items-center justify-center gap-1.5 transition-colors">
                              <TrendingUp className="w-4 h-4" /> Ponów publikację
                            </button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); deleteCampaign(c.id); }}
                            className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-400 hover:bg-rose-600 hover:text-white rounded-xl text-sm font-bold border border-slate-700 hover:border-rose-500 flex items-center justify-center gap-1.5 transition-colors">
                            <Trash2 className="w-4 h-4" /> Usuń Kampanię
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )})}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && <div className="animate-in fade-in"><AdsBoosterSettings onUpdate={fetchProducts} /></div>}
      </div>

      {/* Product Budget Modal */}
      {showBudgetModal && (
        <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-xl z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f172a] rounded-[2rem] w-full max-w-md border border-slate-700/60 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex justify-between items-start">
              <div>
                <h3 className="text-2xl font-black text-white">{isCarousel ? 'Kampania Karuzelowa' : 'Kampania Produktowa'}</h3>
                <p className="text-blue-100/80 text-sm mt-1">{isCarousel ? `${selectedCarouselProducts.length} produktów Woo` : selectedProduct?.name}</p>
              </div>
              <button onClick={() => { setShowBudgetModal(false); setIsCarousel(false); }} className="w-8 h-8 bg-black/20 hover:bg-black/40 rounded-full flex items-center justify-center text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-300 mb-2">Strona Facebook (Fanpage)</label>
                <select 
                  value={selectedPageId} 
                  onChange={e => setSelectedPageId(e.target.value)} 
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="" disabled>Wybierz stronę...</option>
                  {socialAccounts.map(acc => (
                    <option key={acc.id} value={acc.page_id}>{acc.page_name}</option>
                  ))}
                </select>
                {socialAccounts.length === 0 && (
                  <p className="text-xs text-rose-400 mt-2 font-bold">Brak podłączonych stron. Przejdź do Ustawień Ads.</p>
                )}
              </div>
              <div>
                <label className="flex justify-between mb-2"><span className="text-sm font-bold text-slate-300">Budżet Dzienny</span><span className="text-lg font-black text-white">{prodBudget} PLN</span></label>
                <input type="range" min={5} max={500} step={5} value={prodBudget} onChange={e => setProdBudget(Number(e.target.value))} className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-500" />
              </div>
              <div>
                <label className="flex justify-between mb-2"><span className="text-sm font-bold text-slate-300">Dni Emisji</span><span className="text-lg font-black text-white">{prodDuration}</span></label>
                <input type="range" min={1} max={30} value={prodDuration} onChange={e => setProdDuration(Number(e.target.value))} className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500" />
              </div>
              <div className="bg-indigo-500/10 p-4 rounded-xl border border-indigo-500/20 flex justify-between items-center">
                <span className="text-sm text-indigo-300 font-bold">Koszt Max</span>
                <span className="text-2xl font-black text-white">{prodBudget * prodDuration} PLN</span>
              </div>
            </div>
            <div className="p-4 bg-slate-900 border-t border-slate-800 flex gap-3">
              <button onClick={() => setShowBudgetModal(false)} className="px-5 py-3 rounded-xl font-bold text-slate-300 hover:bg-slate-800">Anuluj</button>
              <button onClick={publishProductCampaign} className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-3 rounded-xl">Opublikuj w Meta</button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal?.show && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f172a] rounded-3xl w-full max-w-2xl border border-rose-500/50 shadow-2xl p-8 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-slate-800">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center"><AlertCircle className="w-5 h-5 text-rose-500" /></div>
              <h3 className="text-xl font-bold text-rose-400">Błąd publikacji kampanii</h3>
            </div>
            <div className="bg-black/50 p-4 rounded-xl font-mono text-sm text-rose-300/80 whitespace-pre-wrap mb-4 border border-rose-900/30">{errorModal.error}</div>
            {errorModal.log && errorModal.log.length > 0 && (
              <div className="space-y-1 mb-4">
                <p className="text-sm font-bold text-slate-300 mb-2">Pipeline Log:</p>
                {errorModal.log.map((l: any, i: number) => (
                  <div key={i} className="flex gap-2 text-xs font-mono">
                    <span className="text-slate-500">{l.ts?.split('T')[1]?.split('.')[0]}</span>
                    <span className={`font-bold ${l.step.includes('error') ? 'text-rose-400' : 'text-emerald-400'}`}>[{l.step}]</span>
                    <span className="text-slate-300">{l.msg}</span>
                    {l.meta_id && <span className="text-indigo-400">→ {l.meta_id}</span>}
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setErrorModal(null)} className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl">Zamknij</button>
          </div>
        </div>
      )}
    </div>
  );
}
